<template>
  <div class="space-y-2">
    <!-- Engine & Performance -->
    <div class="border border-border rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        @click="toggleSection('engine')"
      >
        <div class="flex items-center space-x-3">
          <Gauge class="w-5 h-5 text-blue-600" />
          <span class="font-medium">Engine & Performance</span>
        </div>
        <ChevronDown 
          class="w-5 h-5 text-muted-foreground transition-transform duration-200"
          :class="{ 'transform rotate-180': openSections.includes('engine') }"
        />
      </button>
      <div 
        v-show="openSections.includes('engine')"
        class="border-t border-border bg-background p-4"
      >
        <div class="space-y-3">
          <div v-if="specifications?.engine?.displacement" class="flex justify-between">
            <span class="text-muted-foreground">Displacement</span>
            <span class="font-medium">{{ specifications.engine.displacement }}</span>
          </div>
          <div v-if="specifications?.engine?.power" class="flex justify-between">
            <span class="text-muted-foreground">Power</span>
            <span class="font-medium">{{ specifications.engine.power }}</span>
          </div>
          <div v-if="specifications?.engine?.torque" class="flex justify-between">
            <span class="text-muted-foreground">Torque</span>
            <span class="font-medium">{{ specifications.engine.torque }}</span>
          </div>
          <div v-if="specifications?.engine?.cylinders" class="flex justify-between">
            <span class="text-muted-foreground">Cylinders</span>
            <span class="font-medium">{{ specifications.engine.cylinders }}</span>
          </div>
          <div v-if="specifications?.engine?.fuel_type" class="flex justify-between">
            <span class="text-muted-foreground">Fuel Type</span>
            <span class="font-medium">{{ specifications.engine.fuel_type }}</span>
          </div>
          <div v-if="specifications?.performance?.acceleration" class="flex justify-between">
            <span class="text-muted-foreground">0-100km/h</span>
            <span class="font-medium">{{ specifications.performance.acceleration }}</span>
          </div>
          <div v-if="specifications?.performance?.fuel_consumption?.combined" class="flex justify-between">
            <span class="text-muted-foreground">Fuel Consumption</span>
            <span class="font-medium">{{ specifications.performance.fuel_consumption.combined }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Dimensions -->
    <div class="border border-border rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        @click="toggleSection('dimensions')"
      >
        <div class="flex items-center space-x-3">
          <ExpandIcon class="w-5 h-5 text-green-600" />
          <span class="font-medium">Dimensions</span>
        </div>
        <ChevronDown 
          class="w-5 h-5 text-muted-foreground transition-transform duration-200"
          :class="{ 'transform rotate-180': openSections.includes('dimensions') }"
        />
      </button>
      <div 
        v-show="openSections.includes('dimensions')"
        class="border-t border-border bg-background p-4"
      >
        <div class="space-y-3">
          <div v-if="specifications?.dimensions?.length" class="flex justify-between">
            <span class="text-muted-foreground">Length</span>
            <span class="font-medium">{{ specifications.dimensions.length }}</span>
          </div>
          <div v-if="specifications?.dimensions?.width" class="flex justify-between">
            <span class="text-muted-foreground">Width</span>
            <span class="font-medium">{{ specifications.dimensions.width }}</span>
          </div>
          <div v-if="specifications?.dimensions?.height" class="flex justify-between">
            <span class="text-muted-foreground">Height</span>
            <span class="font-medium">{{ specifications.dimensions.height }}</span>
          </div>
          <div v-if="specifications?.dimensions?.wheelbase" class="flex justify-between">
            <span class="text-muted-foreground">Wheelbase</span>
            <span class="font-medium">{{ specifications.dimensions.wheelbase }}</span>
          </div>
          <div v-if="specifications?.dimensions?.ground_clearance" class="flex justify-between">
            <span class="text-muted-foreground">Ground Clearance</span>
            <span class="font-medium">{{ specifications.dimensions.ground_clearance }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Drivetrain -->
    <div class="border border-border rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        @click="toggleSection('drivetrain')"
      >
        <div class="flex items-center space-x-3">
          <Settings class="w-5 h-5 text-purple-600" />
          <span class="font-medium">Drivetrain</span>
        </div>
        <ChevronDown 
          class="w-5 h-5 text-muted-foreground transition-transform duration-200"
          :class="{ 'transform rotate-180': openSections.includes('drivetrain') }"
        />
      </button>
      <div 
        v-show="openSections.includes('drivetrain')"
        class="border-t border-border bg-background p-4"
      >
        <div class="space-y-3">
          <div v-if="specifications?.drivetrain?.drive_type" class="flex justify-between">
            <span class="text-muted-foreground">Drive Type</span>
            <span class="font-medium">{{ specifications.drivetrain.drive_type }}</span>
          </div>
          <div v-if="specifications?.drivetrain?.transmission" class="flex justify-between">
            <span class="text-muted-foreground">Transmission</span>
            <span class="font-medium">{{ specifications.drivetrain.transmission }}</span>
          </div>
          <div v-if="specifications?.drivetrain?.differentials" class="flex justify-between">
            <span class="text-muted-foreground">Differentials</span>
            <span class="font-medium">{{ specifications.drivetrain.differentials }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Safety -->
    <div class="border border-border rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        @click="toggleSection('safety')"
      >
        <div class="flex items-center space-x-3">
          <Shield class="w-5 h-5 text-yellow-600" />
          <span class="font-medium">Safety</span>
        </div>
        <ChevronDown 
          class="w-5 h-5 text-muted-foreground transition-transform duration-200"
          :class="{ 'transform rotate-180': openSections.includes('safety') }"
        />
      </button>
      <div 
        v-show="openSections.includes('safety')"
        class="border-t border-border bg-background p-4"
      >
        <div class="space-y-3">
          <div v-if="specifications?.safety?.airbags" class="flex justify-between">
            <span class="text-muted-foreground">Airbags</span>
            <span class="font-medium">{{ specifications.safety.airbags }}</span>
          </div>
          <div v-if="specifications?.safety?.ancap_rating" class="flex justify-between">
            <span class="text-muted-foreground">ANCAP Rating</span>
            <span class="font-medium">{{ specifications.safety.ancap_rating }}</span>
          </div>
          <div v-if="specifications?.safety?.stability_systems" class="flex justify-between">
            <span class="text-muted-foreground">Stability Systems</span>
            <span class="font-medium">{{ specifications.safety.stability_systems }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Interior -->
    <div class="border border-border rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        @click="toggleSection('interior')"
      >
        <div class="flex items-center space-x-3">
          <Users class="w-5 h-5 text-indigo-600" />
          <span class="font-medium">Interior</span>
        </div>
        <ChevronDown 
          class="w-5 h-5 text-muted-foreground transition-transform duration-200"
          :class="{ 'transform rotate-180': openSections.includes('interior') }"
        />
      </button>
      <div 
        v-show="openSections.includes('interior')"
        class="border-t border-border bg-background p-4"
      >
        <div class="space-y-3">
          <div v-if="specifications?.interior?.seating" class="flex justify-between">
            <span class="text-muted-foreground">Seating</span>
            <span class="font-medium">{{ specifications.interior.seating }}</span>
          </div>
          <div v-if="specifications?.interior?.climate" class="flex justify-between">
            <span class="text-muted-foreground">Climate Control</span>
            <span class="font-medium">{{ specifications.interior.climate }}</span>
          </div>
          <div v-if="specifications?.interior?.infotainment" class="flex justify-between">
            <span class="text-muted-foreground">Infotainment</span>
            <span class="font-medium">{{ specifications.interior.infotainment }}</span>
          </div>
          <div v-if="specifications?.interior?.audio" class="flex justify-between">
            <span class="text-muted-foreground">Audio System</span>
            <span class="font-medium">{{ specifications.interior.audio }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Suspension -->
    <div class="border border-border rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        @click="toggleSection('suspension')"
      >
        <div class="flex items-center space-x-3">
          <Waves class="w-5 h-5 text-gray-600" />
          <span class="font-medium">Suspension</span>
        </div>
        <ChevronDown 
          class="w-5 h-5 text-muted-foreground transition-transform duration-200"
          :class="{ 'transform rotate-180': openSections.includes('suspension') }"
        />
      </button>
      <div 
        v-show="openSections.includes('suspension')"
        class="border-t border-border bg-background p-4"
      >
        <div class="space-y-3">
          <div v-if="specifications?.suspension?.front" class="flex justify-between">
            <span class="text-muted-foreground">Front</span>
            <span class="font-medium">{{ specifications.suspension.front }}</span>
          </div>
          <div v-if="specifications?.suspension?.rear" class="flex justify-between">
            <span class="text-muted-foreground">Rear</span>
            <span class="font-medium">{{ specifications.suspension.rear }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Brakes -->
    <div class="border border-border rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        @click="toggleSection('brakes')"
      >
        <div class="flex items-center space-x-3">
          <Circle class="w-5 h-5 text-red-600" />
          <span class="font-medium">Brakes</span>
        </div>
        <ChevronDown 
          class="w-5 h-5 text-muted-foreground transition-transform duration-200"
          :class="{ 'transform rotate-180': openSections.includes('brakes') }"
        />
      </button>
      <div 
        v-show="openSections.includes('brakes')"
        class="border-t border-border bg-background p-4"
      >
        <div class="space-y-3">
          <div v-if="specifications?.brakes?.front" class="flex justify-between">
            <span class="text-muted-foreground">Front</span>
            <span class="font-medium">{{ specifications.brakes.front }}</span>
          </div>
          <div v-if="specifications?.brakes?.rear" class="flex justify-between">
            <span class="text-muted-foreground">Rear</span>
            <span class="font-medium">{{ specifications.brakes.rear }}</span>
          </div>
          <div v-if="specifications?.brakes?.safety_systems" class="flex justify-between">
            <span class="text-muted-foreground">Safety Systems</span>
            <span class="font-medium">{{ specifications.brakes.safety_systems }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Wheels -->
    <div class="border border-border rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        @click="toggleSection('wheels')"
      >
        <div class="flex items-center space-x-3">
          <Circle class="w-5 h-5 text-gray-500" />
          <span class="font-medium">Wheels</span>
        </div>
        <ChevronDown 
          class="w-5 h-5 text-muted-foreground transition-transform duration-200"
          :class="{ 'transform rotate-180': openSections.includes('wheels') }"
        />
      </button>
      <div 
        v-show="openSections.includes('wheels')"
        class="border-t border-border bg-background p-4"
      >
        <div class="space-y-3">
          <div v-if="specifications?.wheels?.size" class="flex justify-between">
            <span class="text-muted-foreground">Size</span>
            <span class="font-medium">{{ specifications.wheels.size }}</span>
          </div>
          <div v-if="specifications?.wheels?.tyres" class="flex justify-between">
            <span class="text-muted-foreground">Tyres</span>
            <span class="font-medium">{{ specifications.wheels.tyres }}</span>
          </div>
          <div v-if="specifications?.wheels?.spare_wheel" class="flex justify-between">
            <span class="text-muted-foreground">Spare Wheel</span>
            <span class="font-medium">{{ specifications.wheels.spare_wheel }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Exterior -->
    <div class="border border-border rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        @click="toggleSection('exterior')"
      >
        <div class="flex items-center space-x-3">
          <Car class="w-5 h-5 text-blue-500" />
          <span class="font-medium">Exterior</span>
        </div>
        <ChevronDown 
          class="w-5 h-5 text-muted-foreground transition-transform duration-200"
          :class="{ 'transform rotate-180': openSections.includes('exterior') }"
        />
      </button>
      <div 
        v-show="openSections.includes('exterior')"
        class="border-t border-border bg-background p-4"
      >
        <div class="space-y-3">
          <div v-if="specifications?.exterior?.lighting" class="flex justify-between">
            <span class="text-muted-foreground">Lighting</span>
            <span class="font-medium">{{ specifications.exterior.lighting }}</span>
          </div>
          <div v-if="specifications?.exterior?.accessories" class="flex justify-between">
            <span class="text-muted-foreground">Accessories</span>
            <span class="font-medium">{{ specifications.exterior.accessories }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Warranty (only show if valid warranty info exists) -->
    <div v-if="hasValidWarrantyInfo" class="border border-border rounded-lg overflow-hidden">
      <button
        class="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
        @click="toggleSection('warranty')"
      >
        <div class="flex items-center space-x-3">
          <FileCheck class="w-5 h-5 text-green-500" />
          <span class="font-medium">Warranty</span>
        </div>
        <ChevronDown 
          class="w-5 h-5 text-muted-foreground transition-transform duration-200"
          :class="{ 'transform rotate-180': openSections.includes('warranty') }"
        />
      </button>
      <div 
        v-show="openSections.includes('warranty')"
        class="border-t border-border bg-background p-4"
      >
        <div class="space-y-3">
          <div v-if="isValidWarranty(specifications?.warranty?.vehicle)" class="flex justify-between">
            <span class="text-muted-foreground">Vehicle</span>
            <span class="font-medium">{{ specifications.warranty.vehicle }}</span>
          </div>
          <div v-if="isValidWarranty(specifications?.warranty?.powertrain)" class="flex justify-between">
            <span class="text-muted-foreground">Powertrain</span>
            <span class="font-medium">{{ specifications.warranty.powertrain }}</span>
          </div>
          <div v-if="isValidWarranty(specifications?.warranty?.corrosion)" class="flex justify-between">
            <span class="text-muted-foreground">Corrosion</span>
            <span class="font-medium">{{ specifications.warranty.corrosion }}</span>
          </div>
          <div v-if="isValidWarranty(specifications?.warranty?.roadside_assistance)" class="flex justify-between">
            <span class="text-muted-foreground">Roadside Assistance</span>
            <span class="font-medium">{{ specifications.warranty.roadside_assistance }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Important Disclaimer -->
    <div class="mt-6 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
      <div class="flex items-start space-x-3">
        <AlertTriangle class="w-5 h-5 text-amber-600 dark:text-amber-500 mt-0.5 flex-shrink-0" />
        <div>
          <h4 class="font-medium text-amber-800 dark:text-amber-300 mb-1">
            Important Disclaimer
          </h4>
          <p class="text-sm text-amber-700 dark:text-amber-400">
            These specifications are provided as a guide only and may not reflect the exact configuration of this specific vehicle. 
            Vehicle specifications, features, and equipment can vary by model year, trim level, and market. We strongly recommend 
            confirming all specifications, features, and availability with our dealership before making any purchasing decisions. 
            Contact our sales team for the most accurate and up-to-date information about this vehicle.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { 
  ChevronDown, 
  Gauge, 
  Expand as ExpandIcon, 
  Settings, 
  Shield, 
  Users, 
  Waves, 
  Circle, 
  Car, 
  FileCheck,
  AlertTriangle
} from 'lucide-vue-next'

interface Props {
  specifications?: {
    engine?: any
    performance?: any
    dimensions?: any
    drivetrain?: any
    safety?: any
    interior?: any
    suspension?: any
    brakes?: any
    wheels?: any
    exterior?: any
    warranty?: any
  }
}

const props = withDefaults(defineProps<Props>(), {
  specifications: () => ({})
})

const openSections = ref<string[]>([])

const toggleSection = (section: string) => {
  const index = openSections.value.indexOf(section)
  if (index === -1) {
    openSections.value.push(section)
  } else {
    openSections.value.splice(index, 1)
  }
}

// Check if a warranty value is valid (not empty, null, or negative message)
const isValidWarranty = (value: string | undefined | null): boolean => {
  if (!value || typeof value !== 'string') return false
  
  const normalizedValue = value.toLowerCase().trim()
  
  // Check for negative warranty messages
  const negativeMessages = [
    'no manufacturer warranty',
    'no warranty',
    'not applicable',
    'n/a',
    'none',
    'no coverage',
    'not covered',
    'no manufacturer coverage',
    'standard manufacturer warranty' // This is too generic
  ]
  
  return !negativeMessages.some(msg => normalizedValue.includes(msg)) && normalizedValue.length > 0
}

// Check if any warranty information is valid
const hasValidWarrantyInfo = computed(() => {
  const warranty = props.specifications?.warranty
  if (!warranty) return false
  
  return isValidWarranty(warranty.vehicle) ||
         isValidWarranty(warranty.powertrain) ||
         isValidWarranty(warranty.corrosion) ||
         isValidWarranty(warranty.roadside_assistance)
})
</script>